import React from "react";
import {
  Avatar,
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

/**
 * Real quotes from organizers who have run events on BadgeReg.
 *
 * Deliberately NOT marked up as schema.org Review or AggregateRating. Self-serving review markup (
 * a site publishing star ratings about itself) is against Google's structured data guidelines and
 * risks a manual action, which would cost far more traffic than a rating snippet would win. The
 * quotes are here to persuade a human reader.
 */
const testimonials = [
  {
    name: "Jessica Musson",
    title: "Associate Advisor, Crew 324",
    message:
      "BadgeReg has made it so much easier to manage our crew's merit badge colleges. We love it!",
  },
  {
    name: "Crystal Carr",
    title: "Event Coordinator, Troop 959",
    message:
      "BadgeReg has been a game changer for our troop. We can't imagine going back to the old way of doing things!",
  },
];

/** "JM" from "Jessica Musson": a stand-in for the photos we do not have and would not invent. */
function initials(name) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);
}

const Testimonials = () => {
  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          What organizers say
        </Typography>
        <Typography
          align="center"
          color="text.secondary"
          sx={{ mb: { xs: 3, md: 5 } }}
        >
          From the volunteers running merit badge colleges on BadgeReg.
        </Typography>
        <Grid container spacing={3}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} md={6} key={testimonial.name}>
              <Paper
                elevation={0}
                component="figure"
                sx={{
                  m: 0,
                  p: 3,
                  height: "100%",
                  backgroundColor: "background.default",
                  border: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <FormatQuoteIcon
                  aria-hidden="true"
                  sx={{ color: "primary.main", fontSize: 36, mb: 1 }}
                />
                {/* Quote before attribution: it is the part the reader is here for, and a blockquote
                    followed by its figcaption is the structure a screen reader expects. */}
                <Typography
                  component="blockquote"
                  sx={{
                    m: 0,
                    mb: 2,
                    flexGrow: 1,
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    lineHeight: 1.7,
                  }}
                >
                  “{testimonial.message}”
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  component="figcaption"
                >
                  <Avatar
                    aria-hidden="true"
                    sx={{
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      width: 40,
                      height: 40,
                      fontSize: "0.875rem",
                    }}
                  >
                    {initials(testimonial.name)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.title}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Testimonials;
